import { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';
import { 
  Target, Plus, Edit, Trash2, Calendar, User, Tag, DollarSign, 
  ChevronDown, CheckCircle, Clock, AlertTriangle, Pause, X, Search,
  Filter, LayoutGrid, List, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const INITIATIVE_TYPES = [
  { value: 'vulnerability_management', label: 'Vulnerability Management' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'infrastructure', label: 'Infrastructure Security' },
  { value: 'application_security', label: 'Application Security' },
  { value: 'identity_access', label: 'Identity & Access Management' },
  { value: 'incident_response', label: 'Incident Response' },
  { value: 'security_awareness', label: 'Security Awareness' },
  { value: 'network_security', label: 'Network Security' },
  { value: 'data_protection', label: 'Data Protection' },
  { value: 'cloud_security', label: 'Cloud Security' },
  { value: 'other', label: 'Other' },
];

const STAGES = [
  { value: 'business_consideration', label: 'Under Business Consideration', color: 'bg-slate-500/20 text-slate-400' },
  { value: 'under_review', label: 'Under Review', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'approved', label: 'Approved', color: 'bg-indigo-500/20 text-indigo-400' },
  { value: 'yet_to_initiate', label: 'Yet to be Initiated', color: 'bg-yellow-500/20 text-yellow-400' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-cyan-500/20 text-cyan-400' },
  { value: 'deployed', label: 'Deployed', color: 'bg-purple-500/20 text-purple-400' },
  { value: 'completed', label: 'Completed', color: 'bg-emerald-500/20 text-emerald-400' },
  { value: 'closed_no_implementation', label: 'Closed (No Implementation)', color: 'bg-red-500/20 text-red-400' },
  { value: 'on_hold', label: 'On Hold', color: 'bg-orange-500/20 text-orange-400' },
];

const PRIORITIES = [
  { value: 'critical', label: 'Critical', color: 'text-red-400' },
  { value: 'high', label: 'High', color: 'text-orange-400' },
  { value: 'medium', label: 'Medium', color: 'text-yellow-400' },
  { value: 'low', label: 'Low', color: 'text-green-400' },
];

export default function SecurityInitiatives() {
  const { isDarkMode } = useTheme();
  const [initiatives, setInitiatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingInitiative, setEditingInitiative] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [viewMode, setViewMode] = useState('kanban');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    initiative_type: 'other',
    stage: 'yet_to_initiate',
    priority: 'medium',
    owner_name: '',
    start_date: '',
    target_date: '',
    progress: 0,
    budget: '',
    tags: '',
    notes: '',
    custom_fields: {}
  });

  useEffect(() => {
    fetchInitiatives();
  }, []);

  const fetchInitiatives = async () => {
    try {
      const response = await api.get('/security-initiatives');
      setInitiatives(response.data);
    } catch (error) {
      toast.error('Failed to fetch security initiatives');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      initiative_type: 'other',
      stage: 'yet_to_initiate',
      priority: 'medium',
      owner_name: '',
      start_date: '',
      target_date: '',
      progress: 0,
      budget: '',
      tags: '',
      notes: '',
      custom_fields: {}
    });
    setEditingInitiative(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate budget is non-negative
    const budgetValue = formData.budget ? parseFloat(formData.budget) : null;
    if (budgetValue !== null && budgetValue < 0) {
      toast.error('Budget cannot be negative');
      return;
    }
    
    const payload = {
      ...formData,
      tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
      budget: budgetValue,
      progress: parseInt(formData.progress) || 0
    };

    try {
      if (editingInitiative) {
        await api.patch(`/security-initiatives/${editingInitiative.id}`, payload);
        toast.success('Initiative updated successfully');
      } else {
        await api.post('/security-initiatives', payload);
        toast.success('Initiative created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchInitiatives();
    } catch (error) {
      toast.error('Failed to save initiative');
    }
  };

  const handleEdit = (initiative) => {
    setEditingInitiative(initiative);
    setFormData({
      name: initiative.name,
      description: initiative.description || '',
      initiative_type: initiative.initiative_type,
      stage: initiative.stage,
      priority: initiative.priority,
      owner_name: initiative.owner_name || '',
      start_date: initiative.start_date || '',
      target_date: initiative.target_date || '',
      progress: initiative.progress || 0,
      budget: initiative.budget?.toString() || '',
      tags: initiative.tags?.join(', ') || '',
      notes: initiative.notes || '',
      custom_fields: initiative.custom_fields || {}
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/security-initiatives/${deletingId}`);
      toast.success('Initiative deleted');
      setDeleteDialogOpen(false);
      setDeletingId(null);
      fetchInitiatives();
    } catch (error) {
      toast.error('Failed to delete initiative');
    }
  };

  const handleStageChange = async (initiativeId, newStage) => {
    try {
      await api.patch(`/security-initiatives/${initiativeId}`, { stage: newStage });
      setInitiatives(prev => prev.map(i => i.id === initiativeId ? { ...i, stage: newStage } : i));
      toast.success('Stage updated');
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  const getStageInfo = (stage) => STAGES.find(s => s.value === stage) || STAGES[3];
  const getTypeLabel = (type) => INITIATIVE_TYPES.find(t => t.value === type)?.label || type;
  const getPriorityInfo = (priority) => PRIORITIES.find(p => p.value === priority) || PRIORITIES[2];

  const filteredInitiatives = initiatives.filter(i => {
    const matchesSearch = i.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         i.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStage = stageFilter === 'all' || i.stage === stageFilter;
    const matchesType = typeFilter === 'all' || i.initiative_type === typeFilter;
    return matchesSearch && matchesStage && matchesType;
  });

  const groupedByStage = STAGES.reduce((acc, stage) => {
    acc[stage.value] = filteredInitiatives.filter(i => i.stage === stage.value);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-indigo-400 font-rajdhani text-xl">Loading initiatives...</div>
      </div>
    );
  }

  return (
    <div data-testid="security-initiatives-container" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`font-rajdhani font-bold text-3xl flex items-center gap-3 ${isDarkMode ? 'text-white text-shadow-neon' : 'text-slate-900'}`}>
            <Target className="w-8 h-8 text-indigo-400" style={{ filter: isDarkMode ? 'drop-shadow(0 0 8px rgba(99, 102, 241, 0.6))' : 'none' }} />
            Security Initiatives
          </h1>
          <p className={`font-inter mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Manage and track your organization's security programs</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-500 font-rajdhani" data-testid="new-initiative-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Initiative
            </Button>
          </DialogTrigger>
          <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white'}`}>
            <DialogHeader>
              <DialogTitle className={`font-rajdhani text-xl flex items-center gap-2 ${isDarkMode ? '' : 'text-slate-900'}`}>
                <Sparkles className="w-5 h-5 text-indigo-400" />
                {editingInitiative ? 'Edit Initiative' : 'Create Security Initiative'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Initiative Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Zero Trust Network Implementation"
                    className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                    required
                  />
                </div>
                
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Type</Label>
                  <Select value={formData.initiative_type} onValueChange={(v) => setFormData({ ...formData, initiative_type: v })}>
                    <SelectTrigger className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={`max-h-60 ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}`}>
                      {INITIATIVE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Stage</Label>
                  <Select value={formData.stage} onValueChange={(v) => setFormData({ ...formData, stage: v })}>
                    <SelectTrigger className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={`max-h-60 ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}`}>
                      {STAGES.map(stage => (
                        <SelectItem key={stage.value} value={stage.value}>{stage.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Priority</Label>
                  <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                    <SelectTrigger className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : ''}>
                      {PRIORITIES.map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Owner</Label>
                  <Input
                    value={formData.owner_name}
                    onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                    placeholder="John Doe"
                    className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                  />
                </div>
                
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Start Date</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                  />
                </div>
                
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Target Date</Label>
                  <Input
                    type="date"
                    value={formData.target_date}
                    onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
                    className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                  />
                </div>
                
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Progress ({formData.progress}%)</Label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={formData.progress}
                    onChange={(e) => setFormData({ ...formData, progress: e.target.value })}
                    className="w-full mt-3 accent-indigo-500"
                  />
                </div>
                
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Budget ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    placeholder="50000"
                    className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                  />
                </div>
                
                <div className="col-span-2">
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe the initiative objectives and scope..."
                    className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                    rows={3}
                  />
                </div>
                
                <div className="col-span-2">
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Tags (comma-separated)</Label>
                  <Input
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="compliance, infrastructure, Q1-2026"
                    className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                  />
                </div>
                
                <div className="col-span-2">
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes, blockers, dependencies..."
                    className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                    rows={2}
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-500">
                  {editingInitiative ? 'Update Initiative' : 'Create Initiative'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className={isDarkMode ? 'border-white/10 text-zinc-300' : 'border-slate-300 text-slate-700'}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className={`rounded-2xl p-5 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
            <Input
              placeholder="Search initiatives..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pl-10 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-slate-50 border-slate-300 text-slate-900'}`}
            />
          </div>
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className={`w-full md:w-48 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-slate-50 border-slate-300 text-slate-900'}`}>
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by stage" />
            </SelectTrigger>
            <SelectContent className={`max-h-60 ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
              <SelectItem value="all">All Stages</SelectItem>
              {STAGES.map(stage => (
                <SelectItem key={stage.value} value={stage.value}>{stage.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className={`w-full md:w-48 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-slate-50 border-slate-300 text-slate-900'}`}>
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent className={`max-h-60 ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
              <SelectItem value="all">All Types</SelectItem>
              {INITIATIVE_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Button
              variant={viewMode === 'kanban' ? 'default' : 'outline'}
              onClick={() => setViewMode('kanban')}
              className={viewMode === 'kanban' ? 'bg-indigo-600' : isDarkMode ? 'border-white/10 text-zinc-300' : 'border-slate-300 text-slate-700'}
              size="sm"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'bg-indigo-600' : isDarkMode ? 'border-white/10 text-zinc-300' : 'border-slate-300 text-slate-700'}
              size="sm"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <div className={`rounded-xl p-4 text-center ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <p className={`text-3xl font-rajdhani font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{initiatives.length}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Total Initiatives</p>
        </div>
        <div className={`rounded-xl p-4 text-center border-l-2 border-cyan-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <p className="text-3xl font-rajdhani font-bold text-cyan-500">
            {initiatives.filter(i => i.stage === 'in_progress').length}
          </p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>In Progress</p>
        </div>
        <div className={`rounded-xl p-4 text-center border-l-2 border-emerald-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <p className="text-3xl font-rajdhani font-bold text-emerald-500">
            {initiatives.filter(i => i.stage === 'completed').length}
          </p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Completed</p>
        </div>
        <div className={`rounded-xl p-4 text-center border-l-2 border-yellow-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <p className="text-3xl font-rajdhani font-bold text-yellow-500">
            {initiatives.filter(i => i.stage === 'yet_to_initiate' || i.stage === 'approved').length}
          </p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Pending Start</p>
        </div>
        <div className={`rounded-xl p-4 text-center border-l-2 border-orange-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <p className="text-3xl font-rajdhani font-bold text-orange-500">
            {initiatives.filter(i => i.stage === 'on_hold').length}
          </p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>On Hold</p>
        </div>
      </div>

      {/* Kanban View */}
      {viewMode === 'kanban' && (
        <div className="w-full">
          {/* Mobile: Vertical stack / Tablet: 2 columns / Desktop: Full Kanban */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:flex 2xl:gap-4 gap-4 2xl:overflow-x-auto 2xl:pb-4">
            {STAGES.slice(0, 7).map(stage => (
              <div key={stage.value} className="flex-shrink-0 2xl:w-72">
                <div className={`rounded-t-xl p-3 ${stage.color.replace('/20', '/30')}`}>
                  <div className="flex items-center justify-between">
                    <span className={`font-rajdhani font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{stage.label}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-white/10' : 'bg-black/10'}`}>
                      {groupedByStage[stage.value]?.length || 0}
                    </span>
                  </div>
                </div>
                <div className={`rounded-b-xl p-3 space-y-3 min-h-[200px] lg:min-h-[300px] ${isDarkMode ? 'glass' : 'bg-white border border-slate-200'}`}>
                  {groupedByStage[stage.value]?.map(initiative => (
                    <div 
                      key={initiative.id}
                      className={`p-4 rounded-xl transition-all ${
                        isDarkMode 
                          ? 'glass-hover border border-white/10 hover:border-indigo-500/30' 
                          : 'bg-slate-50 border border-slate-200 hover:border-indigo-400 hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className={`font-inter font-medium text-sm line-clamp-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{initiative.name}</h4>
                        <div className="flex gap-1 ml-2 flex-shrink-0">
                          <button onClick={() => handleEdit(initiative)} className={`p-1 rounded ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-200'}`}>
                            <Edit className={`w-3 h-3 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`} />
                          </button>
                          <button onClick={() => { setDeletingId(initiative.id); setDeleteDialogOpen(true); }} className={`p-1 rounded ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-slate-200'}`}>
                            <Trash2 className="w-3 h-3 text-red-400" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-1 mb-3">
                        <span className={`px-2 py-0.5 rounded text-[10px] uppercase ${getPriorityInfo(initiative.priority).color} bg-current/10`}>
                          {initiative.priority}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-[10px] ${isDarkMode ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-200 text-slate-600'}`}>
                          {getTypeLabel(initiative.initiative_type).split(' ')[0]}
                        </span>
                      </div>
                      
                      {/* Progress bar */}
                      <div className="mb-2">
                        <div className={`flex justify-between text-[10px] mb-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          <span>Progress</span>
                          <span>{initiative.progress || 0}%</span>
                        </div>
                        <div className={`h-1.5 rounded-full overflow-hidden ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-200'}`}>
                          <div 
                            className="h-full bg-gradient-to-r from-indigo-500 to-cyan-400 rounded-full"
                            style={{ width: `${initiative.progress || 0}%` }}
                          />
                        </div>
                      </div>
                      
                      {initiative.owner_name && (
                        <div className={`flex items-center gap-1 text-[10px] ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          <User className="w-3 h-3" />
                          {initiative.owner_name}
                        </div>
                      )}
                      {initiative.target_date && (
                        <div className={`flex items-center gap-1 text-[10px] mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                          <Calendar className="w-3 h-3" />
                          {new Date(initiative.target_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {(!groupedByStage[stage.value] || groupedByStage[stage.value].length === 0) && (
                    <div className={`text-center py-8 text-xs ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`}>
                      No initiatives
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="glass rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-zinc-900/50">
              <tr className="text-left text-zinc-500 text-xs font-inter">
                <th className="px-4 py-3">Initiative</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Stage</th>
                <th className="px-4 py-3">Priority</th>
                <th className="px-4 py-3">Progress</th>
                <th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3">Target Date</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInitiatives.map(initiative => (
                <tr key={initiative.id} className="border-t border-white/5 hover:bg-white/5 transition-colors">
                  <td className="px-4 py-3">
                    <p className="text-white font-inter text-sm">{initiative.name}</p>
                    {initiative.description && (
                      <p className="text-zinc-500 text-xs line-clamp-1 mt-0.5">{initiative.description}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-zinc-400 text-xs">{getTypeLabel(initiative.initiative_type)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs ${getStageInfo(initiative.stage).color}`}>
                      {getStageInfo(initiative.stage).label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold ${getPriorityInfo(initiative.priority).color}`}>
                      {initiative.priority.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-indigo-500 to-cyan-400"
                          style={{ width: `${initiative.progress || 0}%` }}
                        />
                      </div>
                      <span className="text-xs text-zinc-400">{initiative.progress || 0}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-zinc-400 text-xs">{initiative.owner_name || '-'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-zinc-400 text-xs">
                      {initiative.target_date ? new Date(initiative.target_date).toLocaleDateString() : '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(initiative)} className="h-7 w-7 p-0">
                        <Edit className="w-3 h-3 text-zinc-400" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setDeletingId(initiative.id); setDeleteDialogOpen(true); }} className="h-7 w-7 p-0">
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredInitiatives.length === 0 && (
            <div className="text-center py-12">
              <Target className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">No initiatives found</p>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-zinc-900 border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Initiative?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This action cannot be undone. The initiative and all its data will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-white/10 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 text-white hover:bg-red-500">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
